import { useRef, useEffect } from "react";
import { SelectOption } from "./Select";
import SearchInput from "./SearchInput";
import RoleSearchInput, { CreditTypeOptions } from "./RoleSearchInput";
import Tag from "@/components/Tag";
import { X } from "lucide-react";

export interface CreditGroup {
  role: string;
  role_label: string;
  contributors: SelectOption[];
}

interface CreditEntriesProps {
  groups: CreditGroup[];
  onChange: (groups: CreditGroup[]) => void;
  reloadPropName: string;
  queryParam: string;
  creditTypeOptions: CreditTypeOptions;
  errors?: string;
  baseErrors?: string;
}

const GROUP_KEYS: (keyof CreditTypeOptions)[] = [
  "performing",
  "composition_and_lyrics",
  "production_and_engineering",
];

const CreditEntries: React.FC<CreditEntriesProps> = ({
  groups,
  onChange,
  reloadPropName,
  queryParam,
  creditTypeOptions,
  errors,
  baseErrors,
}) => {
  const prevGroupCountRef = useRef(groups.length);
  const justAddedGroup = groups.length > prevGroupCountRef.current;

  useEffect(() => {
    prevGroupCountRef.current = groups.length;
  });

  const handleAddRole = (roleValue: string) => {
    let roleLabel = "";
    for (const key of GROUP_KEYS) {
      const found = creditTypeOptions[key].find((o) => o.value === roleValue);
      if (found) {
        roleLabel = found.label;
        break;
      }
    }
    onChange([
      ...groups,
      { role: roleValue, role_label: roleLabel, contributors: [] },
    ]);
  };

  const handleRemoveRole = (index: number) => {
    onChange(groups.filter((_, i) => i !== index));
  };

  const handleAddContributor = (groupIndex: number, option: SelectOption) => {
    onChange(
      groups.map((g, i) => {
        if (i !== groupIndex) return g;
        if (g.contributors.some((c) => c.value === option.value)) return g;
        return { ...g, contributors: [...g.contributors, option] };
      }),
    );
  };

  const handleRemoveContributor = (
    groupIndex: number,
    contributorIndex: number,
  ) => {
    onChange(
      groups.map((g, i) => {
        if (i !== groupIndex) return g;
        return {
          ...g,
          contributors: g.contributors.filter(
            (_, ci) => ci !== contributorIndex,
          ),
        };
      }),
    );
  };

  return (
    <div className="flex w-full flex-col gap-6">
      <RoleSearchInput
        value=""
        creditTypeOptions={creditTypeOptions}
        onChange={handleAddRole}
        selectedValues={groups.map((g) => g.role)}
        placeholder="Add a role"
      />
      {errors && (
        <span className="text-peach-600 dark:text-peach-400 text-sm font-semibold">
          {errors}
        </span>
      )}
      {groups.length > 0 && (
        <div className="flex flex-col gap-6">
          {groups.map((group, groupIndex) => (
            <div key={group.role} className="flex flex-col gap-2">
              <div className="flex items-center gap-1.5">
                <span className="text-cream-800 dark:text-cream-400 text-sm font-medium">
                  {group.role_label}
                </span>
                <button
                  type="button"
                  onClick={() => handleRemoveRole(groupIndex)}
                  className="text-cream-600 hover:text-cream-900 dark:text-cream-400 dark:hover:text-cream-100 transition-colors"
                  aria-label={`Remove ${group.role_label}`}
                >
                  <X size={14} />
                </button>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {group.contributors.map((contributor, contributorIndex) => (
                  <Tag
                    key={contributor.value}
                    label={contributor.label}
                    onRemove={() =>
                      handleRemoveContributor(groupIndex, contributorIndex)
                    }
                  />
                ))}
                <SearchInput
                  inline
                  autoFocus={justAddedGroup && groupIndex === groups.length - 1}
                  reloadPropName={reloadPropName}
                  queryParam={queryParam}
                  onSelect={(option) =>
                    handleAddContributor(groupIndex, option)
                  }
                  selectedValues={group.contributors.map((c) => c.value)}
                  placeholder={
                    group.contributors.length === 0
                      ? "Search for contributors"
                      : ""
                  }
                />
              </div>
            </div>
          ))}
        </div>
      )}
      {baseErrors && (
        <span className="text-peach-600 dark:text-peach-400 text-sm font-semibold">
          {baseErrors}
        </span>
      )}
    </div>
  );
};

export default CreditEntries;
